import { ActorSubclass, HttpAgent } from '@icp-sdk/core/agent'
import { Principal } from '@icp-sdk/core/principal'
import { ARB_CHAIN_ID, BASE_CHAIN_ID, ETH_CHAIN_ID } from '../constants/business'
import {
  BOB_ARB_ID,
  BOB_BASE_ID,
  BOB_ETH_ID,
  BOB_ICP_ID,
  CBBTC_ARB_ID,
  CBBTC_BASE_ID,
  CBBTC_ETH_ID,
  CBBTC_ICP_ID,
  CKBTC_ARB_ID,
  CKBTC_BASE_ID,
  CKBTC_ETH_ID,
  CKBTC_ICP_ID,
  GLDT_ARB_ID,
  GLDT_BASE_ID,
  GLDT_ETH_ID,
  GLDT_ICP_ID,
  ICP_ARB_ID,
  ICP_BASE_ID,
  ICP_ETH_ID,
  ICP_ICP_ID,
  USDC_ARB_ID,
  USDC_BASE_ID,
  USDC_ETH_ID,
  USDC_ICP_ID,
  USDT_ETH_ID,
  USDT_ICP_ID,
} from '../constants/tokenIds'
import {
  Asset,
  AssetRequest,
  _SERVICE as OneSecActor,
  idlFactory as oneSecActor,
  Chain as OneSecChain,
  Token,
  TransferFee,
} from '../idls/onesec/onesec.did'
import {
  idlFactory as oneSecCkUnwrapFactory,
  _SERVICE as OneSecCkUnwrapFactory,
} from '../idls/onesec/onesecCkUnwrap.did'
import { Chain, ICToken, PlugToken } from '../interfaces/common'
import { createActor } from '../utils/createActor'
import { getFirstKey } from '../utils/getFirstKey'

export const ONESEC_MINTER_CANISTER = '5okwm-giaaa-aaaar-qbn6a-cai'
export const ONESEC_CK_UNRWAP_CANISTER = '5ikyc-4yaaa-aaaar-qbzka-cai'

const CHAIN_TO_ONESEC_CHAIN: { [chain: string]: string } = {
  [Chain.ICP]: 'ICP',
  [ETH_CHAIN_ID]: 'Ethereum',
  [ARB_CHAIN_ID]: 'Arbitrum',
  [BASE_CHAIN_ID]: 'Base',
}

export const ONESEC_TO_PLUG_MAP: { [chain: string]: { [token: string]: string } } = {
  ICP: {
    USDC: USDC_ICP_ID,
    USDT: USDT_ICP_ID,
    BOB: BOB_ICP_ID,
    CBBTC: CBBTC_ICP_ID,
    CKBTC: CKBTC_ICP_ID,
    ICP: ICP_ICP_ID,
    GLDT: GLDT_ICP_ID,
  },
  Ethereum: {
    USDC: USDC_ETH_ID,
    USDT: USDT_ETH_ID,
    BOB: BOB_ETH_ID,
    CBBTC: CBBTC_ETH_ID,
    CKBTC: CKBTC_ETH_ID,
    ICP: ICP_ETH_ID,
    GLDT: GLDT_ETH_ID,
  },
  Arbitrum: {
    USDC: USDC_ARB_ID,
    BOB: BOB_ARB_ID,
    CBBTC: CBBTC_ARB_ID,
    CKBTC: CKBTC_ARB_ID,
    ICP: ICP_ARB_ID,
    GLDT: GLDT_ARB_ID,
  },
  Base: {
    USDC: USDC_BASE_ID,
    BOB: BOB_BASE_ID,
    CBBTC: CBBTC_BASE_ID,
    CKBTC: CKBTC_BASE_ID,
    ICP: ICP_BASE_ID,
    GLDT: GLDT_BASE_ID,
  },
}

class OneSecClient {
  public agent: HttpAgent
  private helperActor: ActorSubclass<OneSecActor>

  constructor(agent: HttpAgent) {
    this.agent = agent
    this.helperActor = createActor(oneSecActor, {
      canisterId: ONESEC_MINTER_CANISTER,
      agent,
    })
  }

  static oneSecTokenToPlugToken(token: string | undefined, chain: string | undefined) {
    if (token && chain) {
      return ONESEC_TO_PLUG_MAP[chain]?.[token]
    }
  }

  async fetchFees(srcToken: PlugToken, dstToken: PlugToken): Promise<TransferFee | undefined> {
    const pairs = await this.helperActor.get_transfer_fees()

    const srcChain = CHAIN_TO_ONESEC_CHAIN[srcToken.chain]!
    const dstChain = CHAIN_TO_ONESEC_CHAIN[dstToken.chain]!

    const pair = pairs.find(
      (p) =>
        getFirstKey(p.source_chain) === srcChain &&
        getFirstKey(p.destination_chain) === dstChain &&
        getFirstKey(p.source_token) === srcToken.symbol &&
        getFirstKey(p.destination_token) === dstToken.symbol
    )

    return pair
  }

  async fetchHistory(principalId: string, ethAddress: string) {
    const history = await this.helperActor.get_transfers({
      count: 100n,
      skip: 0n,
      accounts: [
        {
          Icp: {
            ICRC: {
              owner: Principal.fromText(principalId),
              subaccount: [],
            },
          },
        },
        {
          Evm: {
            address: ethAddress,
          },
        },
      ],
    })

    return history
  }

  async bridgeFromICP(
    bridgeAmount: bigint,
    ethAddr: string,
    principal: string,
    srcToken: PlugToken,
    dstToken: PlugToken
  ): Promise<bigint> {
    const source: Asset = {
      tx: [],
      token: { [srcToken.symbol]: null } as Token,
      chain: { ICP: null },
      account: {
        Icp: {
          ICRC: {
            owner: Principal.fromText(principal),
            subaccount: [],
          },
        },
      },
      amount: bridgeAmount,
    }

    const dstChain = CHAIN_TO_ONESEC_CHAIN[dstToken.chain]!

    const destination: AssetRequest = {
      token: { [dstToken.symbol]: null } as Token,
      chain: { [dstChain]: null } as OneSecChain,
      account: {
        Evm: {
          address: ethAddr,
        },
      },
      amount: [],
    }

    const response = await this.helperActor.transfer({
      destination,
      source,
    })

    if ('Failed' in response) {
      throw response.Failed.error
    }

    if ('Fetching' in response) {
      return response.Fetching.block_height
    }

    return response.Accepted.id
  }

  async unwrapCK(amount: bigint, principalId: string, fromToken: ICToken) {
    const unwrapActor = createActor<OneSecCkUnwrapFactory>(oneSecCkUnwrapFactory, {
      canisterId: ONESEC_CK_UNRWAP_CANISTER,
      agent: this.agent,
    })

    const response = await unwrapActor.unwrap_ck_to_onesec({
      from: {
        ICRC: {
          owner: Principal.fromText(principalId),
          subaccount: [],
        },
      },
      amount_e6s: amount,
      ledger_canister_id: fromToken.address,
    })

    if ('Ok' in response) {
      return response.Ok
    } else {
      throw new Error(response.Err)
    }
  }
}

export default OneSecClient
